/**
 * 「应用至整单」按钮 + 覆盖模式选择弹层 —— 表格模式子数组动作（`SubArraySpec.renderRowAction`）的渲染件。
 *
 * 【样式：轻量文本按钮（中性）+ 图标】`size="small"` + **`type="text-neutral"`** +
 * `className="!p-0"`，并**保留 `NoticeTextLeft` 图标**（与卡片模式同款图标）。
 * 刻意**不用**卡片模式的实心 `type="checked-neutral"`。
 *
 * 【为什么是这个组合】权益区块（`rowActionColumn: 'first'`）本按钮与「+ 添加权益」落在
 * **同一 cell、同一行**并排，两者若一个实心一个文本会明显不搭，故用轻量文本按钮
 *（`type` 取 `text-neutral` —— 比添加按钮的 `text` 多一档中性态，产品指定）；图标按产品确认保留。
 * ⚠️ **不要**为了「和卡片模式对齐」改回实心；也**不要**顺手删掉图标、或把 `type` 改回 `text`。
 *
 * 【弹层：覆盖模式二选一】气泡卡片形态（与行级「字段应用至整单」
 * `DropdownListApplyToTheWholeOrder` 一致），文案「有内容时是否批量覆盖」+
 * 两个单选「保留原值」（**默认**）/「覆盖原值」。**没有**字段多选。
 *
 * - **保留原值**（`retain`）：仅当目标行该子数组为空时才整份填入；目标行非空则完全不动。
 * - **覆盖原值**（`overwrite`）：整份覆盖到全部合作内容行（= 历史行为）。
 *
 * 判空与写回见 `applyToWholeOrder.ts` 的 `applySubArrayToRows` / `getCoverByCoverMode`。
 *
 * 【⚠️ 必须是 portal 浮层，不得渲染进 cell】弹层若渲染在单元格内会撑高行高，破坏
 * `rowMetrics.sideHeight` 的「唯一判据源」约定（dev.spec 陷阱 5/6）。`Popover` 由 bedrock
 * 挂到 body，天然满足。
 *
 * ⚠️ **`onClick` 不挂在 Button 上**：`Popover` 会克隆子元素并注入自己的触发事件；
 * 触发入口收敛到 `Popover` 自身，与克隆行为无关。
 *
 * ⚠️ 弹层内容**只能 `stopPropagation()`，不得 `preventDefault()`** —— 详见下方 content 内注释
 *（`preventDefault` 会吞掉 radio 的默认行为，导致单选点不中）。
 *
 * 【显隐】由 `SubArraySpec.renderRowAction` 是否为 `undefined` 决定，
 * 引擎侧另叠加「与『+添加{emptyLabel}』同门控」（`subHasAddRow`）—— 见 `buildBedrockColumns`。
 */
import React, { useState } from 'react';
import { Button, Popover, Radio } from '@bedrock/components';
import { NoticeTextLeft } from '@bedrock/icons-react';
import type { CoverMode } from './applyToWholeOrder';

/** 按钮文案（单一来源：三处子数组共用，避免各写一份后漂移） */
export const APPLY_TO_WHOLE_ORDER_TEXT = '应用至整单';

/** 弹层文案（与行级「字段应用至整单」弹层逐字一致） */
const COVER_MODE_LABEL = '有内容时是否批量覆盖';

/**
 * 默认模式 = `retain`（破坏性更小的那一侧）。
 * ⚠️ 每次**打开弹层都要重置**到它（见 `handleVisibleChange`）：否则用户上次选了「覆盖原值」，
 * 下次打开会带着上次的危险选择，容易误覆盖。
 */
const DEFAULT_COVER_MODE: CoverMode = 'retain';

const COVER_MODE_OPTIONS = [
  { name: '保留原值', value: 'retain' },
  { name: '覆盖原值', value: 'overwrite' },
];

export function ApplyToWholeOrderAction({ targetName, onApply }: {
  /** 所在区块名（考核指标 / 必要权益 / 期望权益）：用于按钮的无障碍名，便于区分三个同名按钮 */
  targetName: string;
  /** 用户点「确定」后触发，带回弹层内选中的模式 */
  onApply: (coverMode: CoverMode) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [coverMode, setCoverMode] = useState<CoverMode>(DEFAULT_COVER_MODE);

  /** 打开时重置为默认模式（关闭时不必重置，下次打开会再置一次） */
  const handleVisibleChange = (next: boolean) => {
    if (next) setCoverMode(DEFAULT_COVER_MODE);
    setVisible(next);
  };

  const handleConfirm = () => {
    onApply(coverMode);
    setVisible(false);
  };

  const content = (
    /**
     * ⚠️ 这里**只能** `stopPropagation()`，**不得** `preventDefault()`。
     *
     * `stopPropagation` 是必要的：弹层内容与触发按钮同在一棵树上，不拦截冒泡会被 Popover
     * 当成「点击外部」而收起。
     *
     * 而 `preventDefault` 会连**默认行为**一起取消 —— radio 的选中与随之而来的 `change`
     * 事件正是 click 的默认行为（同坑已在 `DropdownListApplyToTheWholeOrder` 实测记录：
     * 加 preventDefault 时点「覆盖原值」`change` 触发 0 次，受控值永远停在 `retain`，
     * 「覆盖原值」在 UI 上不可达）。只拦截冒泡不影响默认行为。
     */
    <div className="w-[240px]" onClick={(e) => e.stopPropagation()}>
      {/* 与 columnHeaderWidgets 的「批量编辑」弹层同款标签样式，保持两处观感一致 */}
      <div className="text-base text-[var(--text-color-2)] mb-2">{COVER_MODE_LABEL}</div>
      <Radio.Group
        size="small"
        value={coverMode}
        onChange={(e) => setCoverMode(e.target.value)}
        options={COVER_MODE_OPTIONS}
      />
      <div className="flex justify-end gap-2 mt-3">
        <Button size="small" type="checked-neutral" onClick={() => setVisible(false)}>
          取消
        </Button>
        <Button size="small" type="primary" onClick={handleConfirm}>
          确定
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      showArrow={false}
    >
      {/* 轻量文本按钮：size / className 与 renderCells 的「+ 添加{emptyLabel}」同族，
          type 用 `text-neutral`（比添加按钮的 `text` 多一档中性态，产品指定）；
          另保留 NoticeTextLeft 图标；onClick 不挂在这里（见文件头注释） */}
      <Button
        size="small"
        type="text-neutral"
        className="!p-0"
        icon={<NoticeTextLeft />}
        aria-label={`${targetName}${APPLY_TO_WHOLE_ORDER_TEXT}`}
      >
        {APPLY_TO_WHOLE_ORDER_TEXT}
      </Button>
    </Popover>
  );
}

export default ApplyToWholeOrderAction;
